import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent } from '../../components/boutons/button/button.component';
import { LucideAngularModule } from 'lucide-angular';
import { forkJoin, Observable, of } from 'rxjs';
import { AsyncPipe, NgClass, TitleCasePipe } from '@angular/common';
import { ResponsiveService } from '../../services/reponsive.service';
import { TranslateModule } from '@ngx-translate/core';
import { DownloadService } from '../../services/download.service';
import { HttpEvent } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { JobService } from '../../services/job.service';
import { LottieComponent } from 'ngx-lottie';
import { JobFileEnum } from '../../enums/job-file.eum';
import { AutoCapitalizePipe } from '../../pipes/auto-capitalize.pipe';
import { Job } from '../../class/job.class';
import { Store } from '@ngrx/store';
import { selectJobById } from '../../store/jobs/jobs.selects';

@Component({
  selector: 'app-video-editing',
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    LottieComponent,
    NgClass,
    AsyncPipe,
    ButtonComponent,
    LucideAngularModule,
    LottieComponent,
    AutoCapitalizePipe,
    TitleCasePipe,
  ],
  templateUrl: './video-editing.component.html',
  styleUrls: ['./video-editing.component.scss'],
})
export class VideoEditingComponent {
  @ViewChild('audioVocal', { static: false })
  vocalRef!: ElementRef<HTMLAudioElement>;
  @ViewChild('audioMusic', { static: false })
  musicRef!: ElementRef<HTMLAudioElement>;
  @ViewChild('audioTranslated', { static: false })
  translatedRef!: ElementRef<HTMLAudioElement>;
  @ViewChild('videoPlayer', { static: false })
  videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('progressBar', { static: false })
  progressBarRef!: ElementRef<HTMLInputElement>;

  //? Injection des services
  private responsiveService: ResponsiveService = inject(ResponsiveService);
  private jobService: JobService = inject(JobService);
  private downloadService: DownloadService = inject(DownloadService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private store: Store = inject(Store);

  // Responsives
  smallScreen$: Observable<boolean> = this.responsiveService.small$;
  mediumScreen$: Observable<boolean> = this.responsiveService.desktop$;
  largeScreen$: Observable<boolean> = this.responsiveService.largeDesktop$;

  assetsLoaded: boolean = false;

  jobId: string | undefined | null = null;
  taskId: string | undefined | null = null;

  vocalsUrl: string = '';
  translatedAudioUrl: string = '';
  musicUrl: string = '';
  videoCleanUrl: string = '';

  isPlaying: boolean = false;
  defaultAudio: number = 0.2;

  job$: Observable<Job | undefined> = of(undefined);

  audioForm: FormGroup = new FormGroup({
    originalVolume: new FormControl(this.defaultAudio),
    translationVolume: new FormControl(this.defaultAudio),
    musicVolume: new FormControl(this.defaultAudio),
  });

  // pour lancer la vidéo & tout les fichiers son
  play(): void {
    const vocalPlayer: HTMLAudioElement = this.vocalRef.nativeElement;
    const musicPlayer: HTMLAudioElement = this.musicRef.nativeElement;
    const translatedPlayer: HTMLAudioElement = this.translatedRef.nativeElement;
    const videoPlayer: HTMLVideoElement = this.videoRef.nativeElement;
    if (this.isPlaying) {
      vocalPlayer.pause();
      musicPlayer.pause();
      translatedPlayer.pause();
      videoPlayer.pause();
      this.isPlaying = false;
      return;
    }
    vocalPlayer.play();
    musicPlayer.play();
    translatedPlayer.play();
    videoPlayer.play();
    this.isPlaying = true;
  }

  stop(): void {
    const vocalPlayer: HTMLAudioElement = this.vocalRef.nativeElement;
    const musicPlayer: HTMLAudioElement = this.musicRef.nativeElement;
    const translatedPlayer: HTMLAudioElement = this.translatedRef.nativeElement;
    const videoPlayer: HTMLVideoElement = this.videoRef.nativeElement;
    vocalPlayer.pause();
    musicPlayer.pause();
    translatedPlayer.pause();
    videoPlayer.pause();
    vocalPlayer.currentTime = 0;
    musicPlayer.currentTime = 0;
    translatedPlayer.currentTime = 0;
    videoPlayer.currentTime = 0;
    this.isPlaying = false;
  }

  setTime(time: number): void {
    const vocalPlayer: HTMLAudioElement = this.vocalRef.nativeElement;
    const musicPlayer: HTMLAudioElement = this.musicRef.nativeElement;
    const translatedPlayer: HTMLAudioElement = this.translatedRef.nativeElement;
    const videoPlayer: HTMLVideoElement = this.videoRef.nativeElement;
    videoPlayer.currentTime = time;
    vocalPlayer.currentTime = time;
    musicPlayer.currentTime = time;
    translatedPlayer.currentTime = time;
  }

  complete(): void {
    if (!this.jobId || !this.taskId)
      throw new Error('Job ID or Task ID is not defined');
    this.jobService
      .postSoundsParameters(
        parseInt(this.jobId),
        parseInt(this.taskId),
        this.audioForm.value.originalVolume,
        this.audioForm.value.translationVolume,
        this.audioForm.value.musicVolume
      )
      .subscribe(() => {
        this.router.navigate(['/dashboard/job', this.jobId]);
      });
  }

  loadFiles(jobId: number): Observable<void> {
    return new Observable<void>((observer) => {
      const assets: string[] = ['vocals', 'translatedAudio', 'music'];
      const assetsOberservables: Observable<HttpEvent<Blob>>[] = [];
      assets.forEach((vocal: string) =>
        assetsOberservables.push(
          this.downloadService.downloadAsset(jobId, `${vocal}.wav`)
        )
      );
      assetsOberservables.push(
        this.downloadService.downloadAsset(
          jobId,
          JobFileEnum.ORIGINAL_VIDEO_CLEAN
        )
      );
      forkJoin(assetsOberservables).subscribe({
        next: ([vocals, translatedAudio, music, videoClean]) => {
          this.vocalsUrl = this.convertBlobToUrl(vocals) ?? '';
          this.translatedAudioUrl =
            this.convertBlobToUrl(translatedAudio) ?? '';
          this.musicUrl = this.convertBlobToUrl(music) ?? '';
          this.videoCleanUrl = this.convertBlobToUrl(videoClean) ?? '';
          observer.next();
          observer.complete();
        },
        error: (err) => observer.error(err),
      });
    });
  }

  convertBlobToUrl(assetEvent: HttpEvent<Blob>): string | undefined {
    if (assetEvent.type === 4 && 'body' in assetEvent && assetEvent.body) {
      return URL.createObjectURL(assetEvent.body);
    }
    return undefined;
  }

  private initVideoEvents(): void {
    this.videoRef.nativeElement.ontimeupdate = () => {
      const videoPlayer: HTMLVideoElement = this.videoRef.nativeElement;
      const progressBar: HTMLInputElement = this.progressBarRef.nativeElement;

      const currentTime: number = videoPlayer.currentTime ?? 0;
      const duration: number = videoPlayer.duration ?? 1;
      progressBar.max = duration.toString();
      progressBar.value = currentTime.toString();

      progressBar.onchange = (event) => {
        if (event.target) {
          const newTime: number = parseFloat(
            (event.target as HTMLInputElement).value
          );
          this.setTime(newTime);
        }
      };
    };
  }

  ngAfterViewChecked(): void {
    if (this.assetsLoaded && this.videoRef?.nativeElement) {
      this.initVideoEvents();
    }
  }

  ngOnInit(): void {
    this.jobId = this.route.parent?.snapshot.paramMap.get('jobId');
    this.taskId = this.route.snapshot.paramMap.get('taskId');

    if (this.jobId) {
      this.job$ = this.store.select(selectJobById(parseInt(this.jobId)));
      this.loadFiles(parseInt(this.jobId)).subscribe({
        next: () => {
          this.assetsLoaded = true;
        },
        error: (err) => {
          console.error('Error loading assets', err);
        },
      });
    }
  }
}
